// Bộ lọc Thời gian ở các trang đơn (Shop, Đại lý, CTV) — mốc nhanh hoặc tùy chọn từ–đến, lọc theo ngày
// tạo đơn (giờ Việt Nam, tính cả ngày cuối). Tùy chọn dùng ô ngày của trình duyệt: điện thoại mở lịch có
// sẵn của máy. Bộ lọc giữ trên URL (?range=7d · ?range=custom&from=…&to=…) — F5 / gửi link vẫn giữ.

import { useSearchParams } from 'react-router-dom'
import { CalendarDays } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { rangeSummary, resolveDateFilter, vnDateKey, type DateFilterKey } from '@shoptrack/common/date-range'
import { cn } from '@/lib/utils'

export interface DateFilterValue {
  key: DateFilterKey
  from?: string
  to?: string
}

/**
 * Bộ lọc ngày đọc / ghi trên URL. Mốc nhanh chỉ lưu tên mốc (tính lại theo hôm nay — link "Hôm nay" mở hôm
 * sau vẫn là hôm nay); tùy chọn lưu đúng ngày. Các tham số khác trên URL (status…) giữ nguyên.
 */
export function useDateFilterParams(): [DateFilterValue, (next: DateFilterValue) => void] {
  const [params, setParams] = useSearchParams()
  const value = resolveDateFilter(params.get('range'), params.get('from'), params.get('to'))
  const setValue = (d: DateFilterValue) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p)
        const patch: Record<string, string | undefined> = {
          range: d.key === 'all' ? undefined : d.key,
          from: d.key === 'custom' ? d.from : undefined,
          to: d.key === 'custom' ? d.to : undefined,
        }
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v)
          else next.delete(k)
        }
        return next
      },
      { replace: true },
    )
  return [value, setValue]
}

/** Thêm from / to của bộ lọc vào tham số gọi API (GET /orders, orders.csv). */
export function applyDateFilter(params: URLSearchParams, d: DateFilterValue): URLSearchParams {
  if (d.from) params.set('from', d.from)
  if (d.to) params.set('to', d.to)
  return params
}

const OPTIONS: [DateFilterKey, string][] = [
  ['all', 'Mọi thời gian'],
  ['today', 'Hôm nay'],
  ['yesterday', 'Hôm qua'],
  ['7d', '7 ngày qua'],
  ['30d', '30 ngày qua'],
  ['month', 'Tháng này'],
  ['lastMonth', 'Tháng trước'],
  ['custom', 'Tùy chọn…'],
]

const DATE_INPUT =
  'h-11 desktop:h-10 min-w-0 flex-1 rounded-md border border-rule bg-raise px-2.5 text-base text-ink tabular-nums sm:w-40 sm:flex-none desktop:text-sm focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/20'

export function DateRangeFilter({
  value,
  onChange,
  className,
}: {
  value: DateFilterValue
  onChange: (next: DateFilterValue) => void
  className?: string
}) {
  const today = vnDateKey(new Date())
  const { key, from, to } = value

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <Select
        value={key}
        // Chuyển sang "Tùy chọn" thì điền sẵn khoảng của mốc đang chọn — sửa tiếp từ đó
        onValueChange={(k) => onChange(k === 'custom' ? { key: 'custom', from, to } : { key: k as DateFilterKey })}
      >
        {/* Biểu tượng là con trực tiếp của nút — bọc chung với SelectValue thì [&>span]:line-clamp-1 của
            SelectTrigger đẩy chữ xuống dòng dưới biểu tượng */}
        <SelectTrigger
          className="w-auto min-w-[11rem] justify-start gap-2 desktop:h-10 [&>span]:flex-1 [&>span]:text-left"
          aria-label="Lọc theo ngày tạo đơn"
        >
          <CalendarDays className="size-4 shrink-0 text-muted" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {OPTIONS.map(([k, label]) => (
            <SelectItem key={k} value={k}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {key === 'custom' ? (
        <div className="flex w-full items-center gap-1.5 sm:w-auto">
          <input
            type="date"
            aria-label="Từ ngày"
            value={from ?? ''}
            max={to || today}
            onChange={(e) => onChange({ key: 'custom', from: e.target.value || undefined, to })}
            className={DATE_INPUT}
          />
          <span className="text-muted" aria-hidden>
            –
          </span>
          <input
            type="date"
            aria-label="Đến ngày"
            value={to ?? ''}
            min={from}
            max={today}
            onChange={(e) => onChange({ key: 'custom', from, to: e.target.value || undefined })}
            className={DATE_INPUT}
          />
        </div>
      ) : (
        key !== 'all' && <span className="text-xs tabular-nums text-muted">{rangeSummary(from, to)}</span>
      )}
    </div>
  )
}
